import i18n from '../i18n';
import api from './api';
import { trackEvent } from './analytics';
import { reRegistrarIdioma } from './push';

/**
 * Single entry point for changing the app language (F4.3a T6).
 *
 * Every surface (Settings, auth pages, landing, sidebar switch) must go through
 * changeAppLanguage — it is the one place that applies AND persists the change,
 * so no caller can ever apply-without-persisting again (the old "had to kill the
 * app to see Spanish" class of bug).
 *
 * Lives in lib/ instead of i18n.js because api.js imports i18n.js — adding the
 * api import there would create an import cycle.
 *
 * Native names are constants on purpose: a language's own name is never
 * translated ("Español" must read "Español" even when the UI is in English,
 * or the target audience can't find it).
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', nativeName: 'English' },
  { code: 'es', nativeName: 'Español' },
];

/** Collapses region variants (es-MX, en-US) onto the two supported codes. */
export function normalizeLanguage(lng) {
  return (lng || '').toLowerCase().startsWith('es') ? 'es' : 'en';
}

// True once the user explicitly picks a language in THIS app run. After that,
// nothing may re-apply the server profile until the next boot — the most recent
// explicit choice always wins. Module state on purpose: it must outlive any
// component and reset only on a full reload/app start.
let explicitChoiceMade = false;

/**
 * Boot-order rule (closed design, F4.3a-fix):
 * - logged-in non-guest → the SERVER PROFILE language applies at session
 *   acquisition (boot / login / verify / OAuth);
 * - guest or anonymous → the local choice (i18n detector reading localStorage)
 *   stands. Guests can never write the profile and /auth/me defaults their
 *   preferred_language to "en" (auth.py:963), so applying it would stomp the
 *   local choice — and, worse, poison localStorage with "en" for the next boot.
 *   That stomp was the root cause of "language never persists" for guests.
 * - an explicit in-app choice (changeAppLanguage) wins over ALL later profile
 *   applications until the next boot, so a refreshUser() mid-run can never
 *   revert what the user just picked.
 *
 * The i18n detector (caches: ['localStorage']) persists any change this makes.
 */
export function applyProfileLanguage(user) {
  if (explicitChoiceMade) return;
  if (!user || user.is_guest || !user.preferred_language) return;
  const target = normalizeLanguage(user.preferred_language);
  if (normalizeLanguage(i18n.language) === target) return;
  i18n.changeLanguage(target);
}

/**
 * Applies `lang` immediately (react-i18next re-renders every translated surface
 * on i18n.changeLanguage — no reload) and persists it:
 * - always to localStorage ('lang_preference', the i18n detector's cache), so it
 *   survives app restarts and return visits for guests and logged-in users alike;
 * - for logged-in non-guest users, also to the backend via the SAME endpoint
 *   Settings has always used (PATCH /user/preferences), best-effort: a failed
 *   sync never blocks the client-side change.
 */
export async function changeAppLanguage(lang, user = null) {
  const target = normalizeLanguage(lang);
  const current = normalizeLanguage(i18n.language);
  // Mark BEFORE the no-op guard: even re-picking the current language is an
  // explicit choice and must shield against later profile re-application.
  explicitChoiceMade = true;
  if (target === current) return;

  await i18n.changeLanguage(target);
  try {
    localStorage.setItem('lang_preference', target);
  } catch (e) { /* localStorage unavailable (privacy mode) — silent */ }
  trackEvent('language_changed', { from: current, to: target });

  if (user && !user.is_guest) {
    try {
      await api.patch('/user/preferences', { preferred_language: target });
    } catch (e) {
      // Silent — language change still applies client-side; backend sync is best-effort
    }
  }

  // #464: the push notification speaks the PHONE's language, so the stored push token
  // travels again with the new one (guests too: they upload documents as well). Native
  // shell only, never throws, never awaited: nothing here can hold the language change.
  reRegistrarIdioma(target, user);
}
